import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

@Component({
	selector: 'app-image-resizer-preview',
	templateUrl: './image-resizer-preview.component.html',
	styleUrls: ['./image-resizer-preview.component.scss'],
})
export class ImageResizerPreviewComponent {
	@Input() acceptedFileTypes: string[] = ['.jpg', '.jpeg', '.png'];
	@Input() base64Logo: string | null = null;
	@Output() logoFileChange = new EventEmitter<File | null>();
	@ViewChild('canvasDiv', { static: false }) canvasDiv: ElementRef<HTMLDivElement> | null = null;

	private file: File | null = null;
	public zoomLevel: number = 1;
	public prevZoomLevel: number = 1;
	public svgTransform: string = 'translateY(-50%) scale(1)';
	public modalOpen = false;
	public prevBase64CroppedLogo: string | null = null;
	public base64CroppedLogoPreview: string | null = null;

	public onSliderChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		this.zoomLevel = Number(input.value);
		this.updateSvgTransform();
	}

	private updateSvgTransform(): void {
		this.svgTransform = `translateY(-50%) scale(${this.zoomLevel})`;
	}

	private resetZoom(): void {
		this.zoomLevel = 1;
		this.updateSvgTransform();
	}

	public openModal(): void {
		if (!this.base64CroppedLogoPreview) this.base64CroppedLogoPreview = this.base64Logo;
		if (!this.prevBase64CroppedLogo) this.prevBase64CroppedLogo = this.base64CroppedLogoPreview;

		this.modalOpen = true;
	}

	public closeModal(): void {
		this.modalOpen = false;
	}

	public closeEvent(event: 'buttonPrimary' | 'buttonSecondary' | null): void {
		if (event === 'buttonPrimary') {
			this.prevBase64CroppedLogo = this.base64CroppedLogoPreview;
			this.prevZoomLevel = this.zoomLevel;
			this.cropImageAndEmit();
			return;
		}

		this.base64CroppedLogoPreview = this.prevBase64CroppedLogo;
		this.zoomLevel = this.prevZoomLevel;
		this.updateSvgTransform();
		this.closeModal();
	}

	public onLogoCropped(newBase64: string | null): void {
		this.base64Logo = newBase64;
		this.file = this.convertToFile(newBase64);
		this.logoFileChange.emit(this.file);
		this.closeModal();
	}

	private convertToFile(base64: string): File | null {
		if (!base64) return null;

		try {
			const byteString = atob(base64.split(',')[1]);
			const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
			const arrayBuffer = new ArrayBuffer(byteString.length);
			const uint8Array = new Uint8Array(arrayBuffer);

			for (let i = 0; i < byteString.length; i++) {
				uint8Array[i] = byteString.charCodeAt(i);
			}

			// Asegurar que se use PNG para preservar transparencias
			const fileType = mimeString.includes('png') ? 'image/png' : mimeString;
			return new File([arrayBuffer], 'cropped-image.png', { type: fileType });
		} catch (error) {
			console.error('Error converting base64 to file:', error);
			return null;
		}
	}

	public onFileChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files[0]) {
			const file = input.files[0];
			const reader = new FileReader();
			reader.onload = (e: ProgressEvent<FileReader>) => {
				this.base64CroppedLogoPreview = e.target?.result as string;
			};
			reader.readAsDataURL(file);

			// Reiniciar el valor del zoom
			this.resetZoom();
		}
	}

	public cropImageAndEmit(): void {
		if (!this.base64CroppedLogoPreview) return;

		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.src = this.base64CroppedLogoPreview;
		img.onload = () => this.handleImageLoadWithVisualCrop(img);
		img.onerror = () => {
			console.error('Error loading image for cropping');
		};
	}

	private handleImageLoadWithVisualCrop(img: HTMLImageElement): void {
		const canvasDiv = this.canvasDiv?.nativeElement;
		if (!canvasDiv) return;

		// Obtener las dimensiones exactas del contenedor visible
		const containerRect = canvasDiv.getBoundingClientRect();
		const cropWidth = containerRect.width;
		const cropHeight = containerRect.height;

		// Crear canvas con las dimensiones del área visible
		const canvas = this.createCanvas(cropWidth, cropHeight);
		const ctx = canvas.getContext('2d', { alpha: true });
		if (!ctx) return;

		// Calcular cómo se muestra la imagen en el contenedor con object-fit: contain
		const containerRatio = cropWidth / cropHeight;
		const imgRatio = img.width / img.height;

		let displayWidth: number, displayHeight: number;
		let offsetX: number = 0,
			offsetY: number = 0;

		// Simular object-fit: contain
		if (imgRatio > containerRatio) {
			// Imagen más ancha - se ajusta por el ancho
			displayWidth = cropWidth;
			displayHeight = cropWidth / imgRatio;
			offsetY = (cropHeight - displayHeight) / 2; // Centrar verticalmente
		} else {
			// Imagen más alta - se ajusta por la altura
			displayHeight = cropHeight;
			displayWidth = cropHeight * imgRatio;
			offsetX = (cropWidth - displayWidth) / 2; // Centrar horizontalmente
		}

		// Aplicar zoom
		const scaledWidth = displayWidth * this.zoomLevel;
		const scaledHeight = displayHeight * this.zoomLevel;

		// Calcular el offset del zoom (el zoom se centra)
		const zoomOffsetX = (displayWidth - scaledWidth) / 2;
		const zoomOffsetY = (displayHeight - scaledHeight) / 2;

		// Posición final de la imagen escalada
		const finalX = offsetX + zoomOffsetX;
		const finalY = offsetY + zoomOffsetY;

		// Limpiar canvas manteniendo transparencia
		ctx.clearRect(0, 0, cropWidth, cropHeight);
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';

		// Configurar clipping para que solo se vea lo que está dentro del contenedor
		ctx.save();
		ctx.beginPath();
		ctx.rect(0, 0, cropWidth, cropHeight);
		ctx.clip();

		// Dibujar la imagen exactamente como se ve en la preview
		ctx.drawImage(
			img,
			0,
			0,
			img.width,
			img.height, // Fuente: imagen completa
			finalX,
			finalY,
			scaledWidth,
			scaledHeight, // Destino: posición y tamaño en el canvas
		);

		ctx.restore();

		const croppedBase64 = canvas.toDataURL('image/png');
		this.onLogoCropped(croppedBase64);
	}

	private createCanvas(width: number, height: number): HTMLCanvasElement {
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;

		// Asegurar que el canvas soporte transparencias
		const ctx = canvas.getContext('2d');
		if (ctx) {
			// Limpiar el canvas con transparencia
			ctx.clearRect(0, 0, width, height);
		}

		return canvas;
	}
}
